import { useEffect, useState } from "react";
import { Checkbox, Form, Input, message, Select } from "antd";
import { I18N_NS } from "Config/i18n";
import { useTranslation } from "react-i18next";
import { Button } from "Components/General/Button";
import { PathDirectorySelect } from "Components/Form/PathDirectorySelect";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { observer } from "mobx-react";
import { projectsDataStore, userInfoStore } from "Stores";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { useForm } from "antd/lib/form/Form";

export interface CreateLinkFolderModalProps extends ModalComponentProps {
  createSymlinkFolder: (sourceId: number, targetId: number, newDirName: string) => Promise<void>;
  sourceFolderId: number;
  initialFolderName: string;
}

interface API_ProjectSelection {
  name: string;
  id: number;
}

export const CreateLinkFolderModal = observer(
  ({ hideModal, createSymlinkFolder, sourceFolderId, initialFolderName }: CreateLinkFolderModalProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const [form] = useForm();
    const [projectSelectVisible, setProjectSelectVisible] = useState(false);
    const [projectSelectUseable, setProjectSelectUseable] = useState(false);

    const [availableProjects, setAvailableProjects] = useState<{ label: string; value: number }[]>([]);
    const [selectedProject, setSelectedProject] = useState<number>(projectsDataStore.projectId);

    const handleProjectSelect = (projectId) => {
      setSelectedProject(projectId);
      form.setFieldsValue({ destination: undefined });
    };

    const onFinish = async (value) => {
      await createSymlinkFolder(sourceFolderId, value.destination, value.name);
      message.success(t("Success"));
      hideModal();
    };

    const onProjectSelectVisible = () => {
      setProjectSelectVisible(!projectSelectVisible);
      if (projectSelectVisible) {
        handleProjectSelect(projectsDataStore.projectId);
      }
    };

    const fetchProjects = async () => {
      const response = await API.getData<API_ProjectSelection[]>(
        API_URL.FOLDERS.getPossibleTargetsForCreatingSymlinks(projectsDataStore.projectId, sourceFolderId)
      ).then((res) => res.map((item) => ({ label: item.name, value: item.id })));
      // if only one project is available - no reason to show the select?
      setProjectSelectUseable(response.length > 1);
      setAvailableProjects(response);
    };

    useEffect(() => {
      if (userInfoStore.isSysAdmin || projectsDataStore.isAdmin) {
        fetchProjects();
      }
    }, [projectsDataStore.isAdmin, userInfoStore.isSysAdmin]);

    return (
      <Form requiredMark={true} form={form} onFinish={onFinish}>
        <Form.Item
          rules={[{ required: true, message: t("This field is required") }]}
          labelCol={{ span: 24 }}
          label={t("Name of linked directory")}
          initialValue={initialFolderName.replace(/^[.\-\d\s]*/, "")}
          name="name"
        >
          <Input allowClear={true} autoFocus={true} />
        </Form.Item>
        <Form.Item
          rules={[{ required: true, message: t("This field is required") }]}
          labelCol={{ span: 24 }}
          label={t("Select destination path")}
          name="destination"
        >
          <PathDirectorySelect projectIdContext={selectedProject} />
        </Form.Item>
        {projectSelectUseable && (
          <>
            <Checkbox value={projectSelectVisible} onChange={onProjectSelectVisible}>
              {t("Link to a different project")}
            </Checkbox>
            {projectSelectVisible && (
              <>
                <div>
                  {t(
                    // eslint-disable-next-line max-len
                    "This will create a link to this folder in the selected project and path. If a link is set in a different project all users with any access to the destination path will be granted read access to this folder and all subfolders and files, as long as there are not explicit rights set for those subfolders. Any subfolder with explicit rights in the original project will be excluded."
                  )}
                </div>
                <Form.Item labelCol={{ span: 24 }} label={t("Destination project")}>
                  <Select<number> options={availableProjects} value={selectedProject} onSelect={handleProjectSelect} />
                </Form.Item>
              </>
            )}
          </>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <Button style={{ marginRight: 10 }} onClick={hideModal}>
            {t("Cancel")}
          </Button>
          <Button htmlType="submit" type="primary">
            {t("Create")}
          </Button>
        </div>
      </Form>
    );
  }
);
